import { useState, useEffect } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

import { NotificationToast } from "../components/Generic/Toast/NotificationToast";
import { useAppState } from "../context/StateContext";
import { Error } from "../models/error";
import { Filters, Searches, Sort, Sorts } from "../models/shipeezi.table";
import { WarehouseMap } from "../models/warehouse.map";
import { MapCell } from "../models/warehouse.mapcell";
import WarehouseMapCellService from "../services/warehouse.map.cell.service";
import WarehouseMapService from "../services/warehouse.map.service";
import WarehousingService from "../services/warehousing.service";
import { checkError } from "../util/checkError";

export interface IUpdateMapCells {
  id: number;
  isActive: boolean;
}

export const useWarehousesMapsHook = () => {
  const { currentUser } = useAppState();

  const { t: translate } = useTranslation();

  const [warehousesMapsData, setWarehousesMapsData] = useState<WarehouseMap[]>([]);
  const [mapToUpdate, setMapToUpdate] = useState<WarehouseMap>();
  const [mapId, setMapId] = useState<number>(0);

  const [filters, setFilters] = useState<Filters>({ isActive: ["1"] });
  const [searches, setSearches] = useState<Searches>([]);
  const [sorting, setSorting] = useState<Sorts>();
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(15);
  const [total, setTotal] = useState<number>(0);
  const [hasError, setHasError] = useState<boolean>();
  const [hasErrorMapCell, setHasErrorMapCell] = useState<boolean>();

  enum MapProperties {
    SECTION = "section",
    WAREHOUSE = "warehouse",
    CONFIG_COLUMN = "configColumn",
    CONFIG_ROW = "configRow",
    CONFIG_LEVEL = "configLevel",
  }

  const handleSort = (sort: Sort[]) => {
    const sorting: Sorts = [];

    sort.forEach((sort) => {
      switch (sort.columnName) {
        case MapProperties.SECTION:
          sorting.push([MapProperties.SECTION, "name", sort.direction]);
          break;
        case MapProperties.WAREHOUSE:
          sorting.push([MapProperties.WAREHOUSE, "name", sort.direction]);
          break;
        case MapProperties.CONFIG_COLUMN:
          sorting.push([MapProperties.CONFIG_COLUMN, "size", sort.direction]);
          break;
        case MapProperties.CONFIG_ROW:
          sorting.push([MapProperties.CONFIG_ROW, "size", sort.direction]);
          break;
        case MapProperties.CONFIG_LEVEL:
          sorting.push([MapProperties.CONFIG_LEVEL, "size", sort.direction]);
          break;
        default:
          sorting.push([sort.columnName, sort.direction]);
      }
    });

    return sorting;
  };

  useEffect(() => {
    if (hasError !== undefined && !hasError) {
      setHasError(undefined);
    }
  }, [hasError]);

  useEffect(() => {
    if (hasErrorMapCell !== undefined && !hasErrorMapCell) {
      setHasErrorMapCell(undefined);
    }
  }, [hasErrorMapCell]);

  const getWarehousesMapsList = () =>
    useQuery(
      ["warehouses.maps.list", page, pageSize, searches, filters, sorting],
      () =>
        WarehouseMapService.getAll({
          page,
          pageSize,
          companyId: currentUser?.companyId,
          sortBy:
            sorting && sorting?.length > 0
              ? JSON.stringify([...sorting, ["updatedAt", "desc"]])
              : JSON.stringify([["updatedAt", "desc"]]),
          include: JSON.stringify([
            { association: "section" },
            {
              association: "warehouse",
            },
            { association: "configRow" },
            { association: "configLevel" },
            { association: "configColumn" },
            {
              association: "mapCells",
              include: [{ association: "itemsJobs" }, { association: "storageBoxes" }],
            },
          ]),
          searchByOr: searches && searches?.length > 0 ? JSON.stringify(searches) : "",
          ...filters,
        }),
      {
        enabled: !!currentUser?.companyId,
        refetchOnWindowFocus: false,
        onSuccess: (response) => {
          if (response && response.data) setWarehousesMapsData(response.data);
        },
      }
    );

  const { refetch: refetchWarehousesMaps, isFetching: isFetchingWarehouseMaps } =
    getWarehousesMapsList();

  const getWarehousesMapsCount = () =>
    useQuery(
      ["warehouses.maps.count", filters, searches],
      () =>
        WarehouseMapService.count({
          companyId: currentUser?.companyId,
          include: JSON.stringify([
            {
              association: "warehouse",
            },
          ]),
          searchByOr: searches && searches?.length > 0 ? JSON.stringify(searches) : "",
          ...filters,
        }),
      {
        enabled: !!currentUser?.companyId,
        refetchOnWindowFocus: false,
        onSuccess: (response) => {
          setTotal(response?.data || 0);
        },
      }
    );

  const handleSearch = (values: Searches) => {
    setSorting(undefined);
    setSearches(values);
    setPage(0);
  };

  const handleFilter = (values: Filters) => {
    setFilters(values);
    setPage(0);
  };

  const handleSorting = (sorts: Sort[]) => {
    setSorting(handleSort(sorts));
  };

  const handleCurrentPage = (page: number) => setPage(page - 1);

  const handleGetDefaultFilters = () => Object.values(filters)?.[0];

  const useCreateWarehouseMap = () => {
    return useMutation(
      ["warehouse.map.create"],
      (warehouseMap: WarehouseMap) =>
        WarehouseMapService.create({ ...warehouseMap, companyId: currentUser?.companyId }),
      {
        onSuccess: (response) => {
          if (response && response.status === 200) {
            NotificationToast.success(translate("operationPerformedSuccessfully"), {
              id: "notification.create.map.success",
            });
            setHasError(false);
            refetchWarehousesMaps();
          }
        },
        onError: (error: AxiosError<Error>) => {
          checkError(error, translate("anErrorOccurredTryAgain"));
          setHasError(true);
        },
      }
    );
  };
  const { mutate: createWarehouseMap, isLoading: isCreating } = useCreateWarehouseMap();

  const useUpdateWarehouseMap = () => {
    return useMutation(
      ["warehouse.map.update"],
      ({ id, warehouseMap }: { id: number; warehouseMap: WarehouseMap }) =>
        WarehouseMapService.update(id, warehouseMap),
      {
        onSuccess: (response) => {
          if (response && response.status === 200) {
            NotificationToast.success(translate("operationPerformedSuccessfully"), {
              id: "request.notification.success.warehouse.map",
            });
            setHasError(false);
          }
        },
        onError: (error: AxiosError<Error>) => {
          checkError(error, translate("anErrorOccurredTryAgain"));
          setHasError(true);
        },
      }
    );
  };
  const { mutate: updateWarehouseMap, isLoading: isUpdating } = useUpdateWarehouseMap();

  const { refetch: refetchWarehouseMap, isFetching: isLoadingMap } = useQuery(
    ["warehouses.maps.get.by.id"],
    () =>
      WarehouseMapService.getById(Number(mapId), {
        include: JSON.stringify([
          { association: "configRow" },
          { association: "configLevel" },
          { association: "configColumn" },
          {
            association: "mapCells",
            include: [
              { association: "itemsJobs" },
              { association: "storageBoxes" },
              { association: "status" },
            ],
          },
        ]),
      }),
    {
      enabled: !!mapId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setMapToUpdate(response.data);
      },
    }
  );

  const useUpdateWarehouseMapCell = (onSucces: (mapCells: IUpdateMapCells[]) => void) => {
    return useMutation(
      ["warehouse.map.cell.update"],
      (warehouseMapCells: IUpdateMapCells[]) =>
        WarehouseMapCellService.updateList(warehouseMapCells as MapCell[]),
      {
        onSuccess: (_, variables) => {
          if (variables) {
            onSucces(variables);
          }
          NotificationToast.success(translate("operationPerformedSuccessfully"), {
            id: "request.notification.success.warehouse.map.cell",
          });
          setHasErrorMapCell(false);
          refetchWarehouseMap();
        },
        onError: (error: AxiosError<Error>) => {
          checkError(error, translate("anErrorOccurredTryAgain"));
          setHasErrorMapCell(true);
        },
      }
    );
  };

  const useClearMapCell = () => {
    return useMutation(
      ["warehouse.map.cell.clear"],
      ({ mapCellId }: { mapCellId: number }) => WarehousingService.clearMapCell({ id: mapCellId }),
      {
        onSuccess: () => {
          NotificationToast.success(translate("operationPerformedSuccessfully"), {
            id: "request.notification.success.storage.box.clear",
          });
          setHasErrorMapCell(false);
          refetchWarehouseMap();
        },
        onError: (error: AxiosError<Error>) => {
          checkError(error, translate("anErrorOccurredTryAgain"));
          setHasErrorMapCell(true);
        },
      }
    );
  };

  return {
    getWarehousesMapsList,
    getWarehousesMapsCount,
    updateWarehouseMap,
    createWarehouseMap,
    useUpdateWarehouseMapCell,
    useClearMapCell,

    page,
    total,
    pageSize,
    hasError,
    hasErrorMapCell,
    warehousesMapsData,
    mapToUpdate,
    isLoadingMap,
    isFetchingWarehouseMaps,
    isUpdating,
    isCreating,

    setPageSize,
    setMapId,
    handleSearch,
    handleFilter,
    handleSorting,
    handleCurrentPage,
    handleGetDefaultFilters,
    refetchWarehousesMaps,
  };
};
